import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import jwt from "jwt-decode";
import { checkLoginUser } from "../../../Store/auth/login/actions";
import { clearStudioWorkspaceCaches } from "../../../helpers/studioSignupSession";
import { useKarkhanaPwa } from "../../../helpers/useCollectorPwa";
import { LangToggle, useKT } from "../i18n/kt";
import InstallKarkhanaApp from "./InstallKarkhanaApp";
import { dropRememberedPhone, hasRememberedPhone, renewOnce, signInError, signInWithPin } from "./supervisorSession";
import "../rounds/karkhana-rounds.scss";
import "./supervisor-login.scss";
import { kp } from "../karkhanaPaths";
import { supervisorMayOpen } from "../../../helpers/karkhanaEnabled";
import { takeReturnTo } from "../../sales/internalQr";

/** A saree label scanned before sign-in (/q/...) is where they wanted to be; anything else goes home. */
const afterSignIn = () => {
  const back = takeReturnTo();
  return back && supervisorMayOpen(back) ? back : kp.home();
};

/**
 * The supervisor's own sign-in page: the link the studio sends on WhatsApp / SMS, and where the
 * installed Karkhana app opens. Not linked from the public site and kept out of search engines.
 * Mobile number + the 6-digit PIN the studio gave; after that the phone stays signed in (this page
 * renews the login on open) until the supervisor logs out. Password sign-in stays for older logins.
 */

const MOBILE_KEY = "hkp.karkhana.loginMobile";

const readMobile = (): string => {
  try {
    return localStorage.getItem(MOBILE_KEY) || "";
  } catch {
    return "";
  }
};
const KarkhanaSupervisorLoginPage = (props: any) => {
  const { t } = useKT();
  const history = useHistory();
  const location = useLocation();
  useKarkhanaPwa();
  const studio = new URLSearchParams(location.search).get("studio") || "";
  const [opening, setOpening] = useState(hasRememberedPhone);
  const [usePassword, setUsePassword] = useState(false);
  const [phone, setPhone] = useState(readMobile);
  const [remember, setRemember] = useState(true);
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const busyRef = useRef(false);

  // Private link: ask search engines not to list it.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const title = document.title;
    document.title = "HKP Karkhana · Supervisor";
    return () => {
      document.head.removeChild(meta);
      document.title = title;
    };
  }, []);

  // A phone that signed in before: get a fresh login and go straight in.
  useEffect(() => {
    if (!hasRememberedPhone()) return;
    let live = true;
    renewOnce().then((ok) => {
      if (!live) return;
      if (ok) history.replace(afterSignIn());
      else setOpening(false);
    });
    return () => {
      live = false;
    };
  }, [history]);

  // PIN sign-in always remembers the phone; the checkbox is for password sign-in.
  const rememberMobile = (keep = remember) => {
    try {
      if (keep) localStorage.setItem(MOBILE_KEY, phone.trim());
      else localStorage.removeItem(MOBILE_KEY);
    } catch {
      // Storage blocked: they type it next time.
    }
  };

  const submitPin = async (digits = pin) => {
    if (busyRef.current) return;
    if (!phone.trim() || digits.length !== 6) {
      setError(t("Enter your mobile number and 6-digit PIN"));
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError("");
    try {
      const res = await signInWithPin(phone.trim(), digits);
      if (res.ok) {
        rememberMobile(true);
        history.replace(afterSignIn());
        return;
      }
      setPin("");
      setError(t(signInError(res.status, res.message)));
    } catch {
      setError(t("Could not reach HKP. Check the internet and try again."));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const submitPassword = () => {
    if (!phone.trim() || !password) {
      setError(t("Enter your mobile number and password"));
      return;
    }
    setBusy(true);
    setError("");
    props.checkLoginUser({ emailOrPhone: phone.trim().toLowerCase(), password }, (res: any) => {
      setBusy(false);
      if (!res?.success) {
        setError(t("Mobile number or password is wrong"));
        return;
      }
      const authUser: any = jwt(res.data.token);
      localStorage.setItem("authUser", JSON.stringify(authUser));
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("refreshToken", res.data.refreshToken);
      dropRememberedPhone();
      clearStudioWorkspaceCaches();
      rememberMobile();
      history.replace(afterSignIn());
    });
  };

  const onPin = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 6);
    setPin(digits);
    setError("");
    // Sixth digit signs in, no extra tap.
    if (digits.length === 6 && phone.replace(/\D/g, "").length >= 10) submitPin(digits);
  };

  if (opening) {
    return (
      <main className="ksl">
        <p className="ksl__opening" role="status">
          {t("Opening…")}
        </p>
      </main>
    );
  }

  return (
    <main className="ksl">
      <div className="ksl__card">
        <div className="ksl__top">
          <p className="ksl__studio">{studio || "HKP"}</p>
          <LangToggle />
        </div>
        <h1 className="ksl__title">{t("Karkhana supervisor")}</h1>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (usePassword) submitPassword();
            else submitPin();
          }}
          noValidate
        >
          <label htmlFor="ksl-phone">{t("Mobile number")}</label>
          <input
            id="ksl-phone"
            type="tel"
            inputMode="tel"
            autoComplete="username"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="98390 12345"
          />
          {usePassword ? (
            <>
              <label htmlFor="ksl-pw">{t("Password")}</label>
              <div className="ksl__pw">
                <input
                  id="ksl-pw"
                  type={show ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button type="button" onClick={() => setShow((v) => !v)} aria-pressed={show}>
                  {show ? t("Hide") : t("Show")}
                </button>
              </div>
            </>
          ) : (
            <>
              <label htmlFor="ksl-pin">{t("PIN (6 digits)")}</label>
              <div className="ksl__pw">
                <input
                  id="ksl-pin"
                  className="ksl__pin"
                  type={show ? "text" : "password"}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={pin}
                  onChange={(e) => onPin(e.target.value)}
                  aria-describedby="ksl-stay"
                />
                <button type="button" onClick={() => setShow((v) => !v)} aria-pressed={show}>
                  {show ? t("Hide") : t("Show")}
                </button>
              </div>
            </>
          )}
          {usePassword ? (
            <label className="ksl__remember">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              {t("Remember this phone")}
            </label>
          ) : null}
          {error ? (
            <p className="ksl__err" role="alert">
              {error}
            </p>
          ) : null}
          <button type="submit" className="ksl__go" disabled={busy}>
            {busy ? t("Signing in…") : t("Sign in")}
          </button>
        </form>
        {usePassword ? null : (
          <p className="ksl__help" id="ksl-stay">
            {t("You stay signed in on this phone until you log out.")}
          </p>
        )}
        <p className="ksl__help">{usePassword ? t("Forgot the password? Ask your studio for a new one.") : t("Forgot the PIN? Ask your studio for a new one.")}</p>
        <button
          type="button"
          className="ksl__switch"
          onClick={() => {
            setUsePassword((v) => !v);
            setError("");
            setShow(false);
          }}
        >
          {usePassword ? t("Sign in with PIN") : t("Sign in with password instead")}
        </button>
      </div>
      <div className="ksl__app">
        <InstallKarkhanaApp />
      </div>
    </main>
  );
};

export default connect(null, { checkLoginUser })(KarkhanaSupervisorLoginPage);
